import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  ButtonType,
  Drawer
} from 'syngenta-digital-cropwise-react-ui-kit';
import ChevronLeftIcon from 'assets/chevron_left.svg?react';
import CloseIcon from 'assets/close.svg?react';
import { useProductFormUnits } from 'screens/Applications&WorkOrders/StepsLayout/Components/ProductsStep/components/FillProductDetailsDrawer/hooks/useProductFormUnits';
import useProduct from 'hooks/useProduct';
import useUomConvert from 'hooks/useUomConvert';
import { calcSpecialCase } from 'helpers/unitConversionHelpers';
import deriveListPriceControls from 'helpers/listPriceControls';
import useInventoryDocumentationData from 'screens/ProductList/ProductDetails/hooks/useInventoryDocumentationData';
import {
  deriveOpeningRate,
  div,
  formatMinPrecision,
  formatQuantityDisplay,
  groupDigits,
  mul,
  sanitizeNumericInput,
  sanitizeRateInput,
  signsAgree,
  signsDiffer
} from './helpers';
import { InvoiceFormProduct } from '../../../../Constants/invoiceFormikData';
import ProductQuantitySection from './ProductQuantitySection';
import ProductCostSection from './ProductCostSection';
import './style.css';

export interface CatalogProduct {
  id: string;
  name: string;
  custom: boolean;
}

interface ProductDetails {
  manufacturer?: string;
  productType?: string;
  stdunit?: string;
  // The price's unit falls back to this, never to the invoiced quantity's unit
  // — matching how Applications and Work Orders seed the same field.
  stdpackageunit?: string;
  // Packages per standard unit. Only meaningful for the package-relative units
  // in `specialCaseUnit` (bag, box, bulk, bushel, …), which convert by this
  // factor rather than dimensionally.
  stdfactor?: number;
  // Density from the product catalog. The invoice service needs a non-zero
  // density to build the compatible-units list for the line item; a 0 density
  // makes create/edit 500 with "The Compatable Units List is empty ... Density
  // = 0.0" (FR-095). Captured here and floored to 1 in the API payload.
  density?: number;
  registrationNumber?: string;
}

interface FillInvoiceProductDrawerProps {
  visible: boolean;
  catalogProduct: CatalogProduct;
  existingProduct?: InvoiceFormProduct;
  canWriteCost: boolean;
  onSave: (product: InvoiceFormProduct) => void;
  onClose: () => void;
  zIndex?: number;
  /** Skip the open/close slide transition (Applications-parity — see ProductsStep). */
  suppressAnimation?: boolean;
  mask?: boolean;
}

const DRAWER_WIDTH = 520;

const FillInvoiceProductDrawer: React.FC<FillInvoiceProductDrawerProps> = ({
  visible,
  catalogProduct,
  existingProduct,
  canWriteCost,
  onSave,
  onClose,
  zIndex,
  suppressAnimation = false,
  mask = true
}) => {
  const units = useProductFormUnits(catalogProduct as any);
  const { fetchProductData, getProduct } = useProduct();
  const convertFn = useUomConvert();
  // FR-108: product label document (rendered as an "Open Product Label" button
  // at the bottom of the Product Quantity section, matching Applications).
  const { dataDocumentation, openFile } = useInventoryDocumentationData(
    catalogProduct.id
  );

  const [productDetails, setProductDetails] = useState<ProductDetails | null>(
    null
  );
  const [productDetailsLoadedFor, setProductDetailsLoadedFor] = useState<
    string | null
  >(null);

  const [totalQuantityValue, setTotalQuantityValue] = useState<number>(
    existingProduct?.totalQuantityValue ?? 0
  );
  const [totalQuantityInput, setTotalQuantityInput] = useState<string>(
    existingProduct?.totalQuantityValue
      ? formatQuantityDisplay(existingProduct.totalQuantityValue)
      : ''
  );
  const [totalQuantityUnit, setTotalQuantityUnit] = useState<string>(
    existingProduct?.totalQuantityUnit ?? ''
  );
  // FR-104 / FR-105 / FR-077: Price per Unit and Total Cost are an interrelated
  // pair modeled exactly like the Applications area inputs (Properties step's
  // Applied Area vs Coverage Percent). We keep the FULL-precision numeric value
  // in state and only round for DISPLAY: while a field is focused it shows the
  // real value the user is editing (via `costPendingEdits`); once blurred it
  // shows `Number(value).toFixed(2)` (2-dp currency, and "0.00" by default).
  // Only the display waits for blur — the values themselves settle on every
  // keystroke. For an existing line item the price is recovered precisely from
  // cost ÷ quantity.
  const [priceValue, setPriceValue] = useState<number>(
    () => deriveOpeningRate(existingProduct).value
  );
  const [totalCostValue, setTotalCostValue] = useState<number>(
    existingProduct?.totalCostValue ?? 0
  );
  const [priceUnit, setPriceUnit] = useState<string>(
    () => deriveOpeningRate(existingProduct).unit
  );
  // The product's standing list price, read from inventory. Zero means it has
  // none, which is what leaves the reset button permanently unavailable.
  // Set once the price unit is settled — either the user picked it, or it was
  // pinned to the invoiced quantity's unit for a recovered rate — so an
  // in-flight fetch cannot relabel it.
  const priceUnitPinnedRef = useRef(false);
  // Set once the user types in Price per Unit, so the list-price prefill cannot
  // overwrite what they entered. A plain `prev || price` cannot tell a typed
  // zero from an untouched field.
  const priceTouchedRef = useRef(false);
  const [listPrice, setListPrice] = useState<number>(0);
  const [listPriceUnit, setListPriceUnit] = useState<string>('');
  const [packageWeight, setPackageWeight] = useState<number | undefined>(
    existingProduct?.packageWeight
  );
  const [saveAsNewListPrice, setSaveAsNewListPrice] = useState<boolean>(
    existingProduct?.saveAsNewListPrice ?? false
  );
  const [focusedCostField, setFocusedCostField] = useState<
    'price' | 'cost' | null
  >(null);
  const [costPendingEdits, setCostPendingEdits] = useState<{
    price?: string;
    cost?: string;
  }>({});
  // Distinguishes a quantity the user typed from one back-computed out of the
  // cost pair. Without it, the first cost keystroke that fills a blank quantity
  // would make the quantity look user-set, and every keystroke after it would
  // overwrite the other cost field the user had just entered.
  const [qtyAutoDerived, setQtyAutoDerived] = useState(false);
  // A line whose quantity and cost already disagree in sign predates the rule
  // that a credit/return line carries the minus on both, so editing its Total
  // Cost must not re-sign the quantity the user never touched — the same
  // exemption `handleQuantityChange` makes in the other direction. Latched when
  // the cost field takes focus rather than read live, because the cost it would
  // compare against is rewritten on every keystroke of the edit in progress.
  // Whether Total Cost is a figure the user typed, rather than one derived from
  // the price and quantity. It decides which side a quantity edit holds still:
  // a cost the user entered is the anchor and the rate follows it, but a derived
  // cost is not — holding it would rewrite a Price per Unit that came from the
  // product's list price, and offer the result up as a new list price.
  const costTypedByUser = useRef(false);
  const pendingConversionRecalculation = useRef<
    | { type: 'quantity'; previousQuantity: number }
    | { type: 'cost' | 'derived'; field: 'price' | 'cost' }
    | { type: 'unit' | 'reset' }
    | null
  >(null);

  // The quantity as it stood when the field was focused, so blur can tell a real
  // edit from a tab-through.
  const quantityAtFocus = useRef<number | null>(null);

  // Clicking from one cost field straight into another fires blur and focus in
  // the same task, so React batches them and the focus handler still sees the
  // pre-blur state — it would seed its editing value from a figure the blur has
  // just replaced. These mirror the two values and are written synchronously
  // alongside the state, so the focus handler reads what the blur decided.
  const livePrice = useRef(priceValue);
  const liveCost = useRef(totalCostValue);
  livePrice.current = priceValue;
  liveCost.current = totalCostValue;

  const applyPrice = (value: number) => {
    livePrice.current = value;
    setPriceValue(value);
  };
  const applyCost = (value: number) => {
    liveCost.current = value;
    setTotalCostValue(value);
  };

  const signsAgreedBeforeCostEdit = useRef(
    signsAgree(
      existingProduct?.totalQuantityValue ?? 0,
      existingProduct?.totalCostValue ?? 0
    )
  );
  const [qtyError, setQtyError] = useState('');
  const [unitError, setUnitError] = useState('');
  const [isUnitDropdownOpen, setIsUnitDropdownOpen] = useState(false);
  const [isPriceUnitDropdownOpen, setIsPriceUnitDropdownOpen] = useState(false);

  const isEditing = !!existingProduct;

  // FR-108: find the "Label" document from the product's documentation so we
  // can offer an "Open Product Label" button (same lookup as Applications).
  const documentDescriptions = (dataDocumentation as {
    documentdescriptions?: Array<{ id: string; description: string }>;
  } | null)?.documentdescriptions;
  const labelDocument = useMemo(() => {
    if (!Array.isArray(documentDescriptions) || !documentDescriptions.length) {
      return null;
    }
    return (
      documentDescriptions.find(doc => doc.description === 'Label') || null
    );
  }, [documentDescriptions]);

  // Fetch manufacturer / product type / standard unit / EPA number from the
  // catalog (custom products) or master product list (non-custom products).
  useEffect(() => {
    if (!visible) return undefined;
    let cancelled = false;

    fetchProductData({
      productId: catalogProduct.id,
      custom: catalogProduct.custom
    }).then((result: { data: ProductDetails } | null) => {
      if (cancelled) return;

      const details = result?.data ?? null;
      setProductDetails(details);
      setProductDetailsLoadedFor(catalogProduct.id);

      if (details) {
        // Default the unit dropdown to the catalog's standard unit, matching
        // Applications & Work Orders (generateInitialValues: `totalProductUnit
        // || stdpackageunit`) — only when the field hasn't already been set
        // (new product, no unit chosen yet).
        const standardUnit = details.stdunit;
        if (!existingProduct?.totalQuantityUnit && standardUnit) {
          setTotalQuantityUnit(prev => prev || standardUnit);
        }
        // Only a fallback. The stored list price's own unit is authoritative
        // and may arrive later, so it is allowed to overwrite this below —
        // otherwise whichever request resolved first would win, and a price
        // quoted per quart could end up shown against gallons.
        const packageUnit = details.stdpackageunit;
        if (packageUnit) {
          setPriceUnit(prev => prev || packageUnit);
        }
      }
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, catalogProduct.id, catalogProduct.custom]);

  // The product's standing list price. A new line opens showing it, which is
  // what makes the controls meaningful — without it there is nothing to compare
  // the entered price against, and nothing for the reset button to restore.
  useEffect(() => {
    if (!visible) return undefined;
    let cancelled = false;

    getProduct(catalogProduct.id).then((data: any) => {
      if (cancelled || !data) return;

      const price = data.averagePricePerUnit ?? 0;
      const unit = data.averagePricePerUnitUnit ?? '';
      setListPrice(price);
      setListPriceUnit(unit);
      setPackageWeight(data.packageWeight ?? undefined);

      if (price > 0) {
        // Only ever fills a blank: a saved line keeps the price it was saved
        // with, and so does anything typed while this request was in flight —
        // including a deliberate zero, which `prev || price` alone would read as
        // an untouched field and replace.
        if (!priceTouchedRef.current) {
          setPriceValue(prev => prev || price);
        }
        // The unit, unlike the price, replaces the catalog fallback: the stored
        // list price is quoted in this unit, and showing it against another
        // would read as a changed price and wrongly enable the controls.
        if (unit) {
          setPriceUnit(prev => (priceUnitPinnedRef.current ? prev : unit));
        }
      }
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, catalogProduct.id]);

  const {
    isResetDisabled,
    canSaveNewListPrice,
    resetValues
  } = deriveListPriceControls({
    listPrice,
    listPriceUnit,
    currentPrice: priceValue,
    currentUnit: priceUnit
  });

  // Guard against a previously-selected unit that is no longer valid for the
  // currently selected product (e.g. a stale value carried over from a
  // different product), so an invalid UOM can never remain selected.
  useEffect(() => {
    if (
      units.length &&
      totalQuantityUnit &&
      !units.some(unit => unit.key === totalQuantityUnit)
    ) {
      setTotalQuantityUnit('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [units]);

  // ProductsStep renders this drawer without a `key`, and FR-155.5 deliberately
  // cancels its pending unmount when re-opened quickly ("save product A,
  // immediately edit product B" must not let A's stale cleanup timer clear B's
  // state) — so the SAME component instance can persist across a close→reopen,
  // possibly for a different `existingProduct`. The useState initializers above
  // only run on the very first-ever mount, so a reused instance would otherwise
  // keep showing the PREVIOUS session's values, and any focus/pending-edit
  // state left over from it (e.g. Total Invoiced Quantity's display not
  // matching its actual focus state, or a stale cost pending-edit leaking into
  // the new session). Resync everything from the current `existingProduct`
  // whenever the drawer transitions to visible, so every session — fresh mount
  // or reused instance alike — starts from the same, correct state.
  useEffect(() => {
    if (!visible) return;
    setTotalQuantityValue(existingProduct?.totalQuantityValue ?? 0);
    setTotalQuantityInput(
      existingProduct?.totalQuantityValue
        ? formatQuantityDisplay(existingProduct.totalQuantityValue)
        : ''
    );
    setTotalQuantityUnit(existingProduct?.totalQuantityUnit ?? '');
    // The price-side state resets with everything else; without this a reused
    // drawer keeps the previous line's unit and tick.
    //
    // A line that carries a rate opens on it. One that does not has its rate
    // divided out of the cost pair, which expresses it in the invoiced
    // quantity's unit — the same fallback the invoice tables use, so the line
    // reads the same wherever it is shown. Pinned once resolved so an in-flight
    // product fetch cannot relabel a figure that came from the line itself.
    const opening = deriveOpeningRate(existingProduct);
    setPriceValue(opening.value);
    setPriceUnit(opening.unit);
    priceUnitPinnedRef.current = Boolean(opening.unit);
    priceTouchedRef.current = false;
    setSaveAsNewListPrice(existingProduct?.saveAsNewListPrice ?? false);
    // `packageWeight` is a fact about the product, not the line, and is owned by
    // the fetch above — clearing it here would drop it for a reused drawer.
    costTypedByUser.current = false;
    pendingConversionRecalculation.current = null;
    setTotalCostValue(existingProduct?.totalCostValue ?? 0);

    setFocusedCostField(null);
    setCostPendingEdits({});
    setQtyAutoDerived(false);
    signsAgreedBeforeCostEdit.current = signsAgree(
      existingProduct?.totalQuantityValue ?? 0,
      existingProduct?.totalCostValue ?? 0
    );
    setQtyError('');
    setUnitError('');
  }, [visible, existingProduct]);

  // FR-076 (add) + FR-107 (edit): autofocus the Total Product input whenever the
  // drawer opens — for BOTH the add and the edit (pencil) entry points. The bare
  // `autoFocus` attribute can miss the edit case (the field mounts behind the
  // drawer's open animation), so focus it explicitly once it is in the DOM,
  // mirroring the Applications drawer's deferred `input.focus()`.
  useEffect(() => {
    if (!visible) return undefined;
    const timer = setTimeout(() => {
      const input = document.querySelector<HTMLInputElement>(
        'input[data-testid="fill-product-quantity"]'
      );
      input?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, [visible]);

  // The invoiced quantity restated in whatever unit the price is quoted in.
  // Everything downstream multiplies or divides against this rather than the
  // raw quantity, which is what lets a line be invoiced in gallons but priced
  // per quart.
  // Routed through `calcSpecialCase` rather than a plain conversion because
  // package-relative units (bag, box, bulk, bushel, …) convert by the product's
  // stdfactor, not dimensionally — a direct convert silently returns the wrong
  // number for them.
  const productDetailsReady = productDetailsLoadedFor === catalogProduct.id;
  const conversionDetails = productDetailsReady ? productDetails : null;
  const convertQuantity = (
    quantity: number,
    fromUnit: string,
    toUnit: string
  ): number => {
    if (!quantity || !fromUnit || !toUnit) return quantity;
    if (fromUnit === toUnit) return quantity;

    try {
      const converted = calcSpecialCase(
        quantity,
        fromUnit,
        toUnit,
        {
          stdunit: conversionDetails?.stdunit,
          stdpackageunit: conversionDetails?.stdpackageunit,
          stdfactor: conversionDetails?.stdfactor,
          density: conversionDetails?.density
        },
        convertFn
      );
      return Number.isFinite(converted) && converted ? converted : Number.NaN;
    } catch {
      // Units with no path between them — a weight priced against a volume for
      // a product carrying no density. Surfaced to the user rather than
      // silently producing a wrong total.
      return Number.NaN;
    }
  };

  // The invoiced quantity restated in whatever unit the price is quoted in.
  const quantityInPriceUnit = (
    quantity: number,
    unit: string = priceUnit
  ): number => convertQuantity(quantity, totalQuantityUnit, unit);

  // The inverse: a figure worked out against the price (cost / rate) is in the
  // price's unit, and has to come back before it can stand as the invoiced
  // quantity.
  const quantityFromPriceUnit = (
    quantity: number,
    unit: string = priceUnit
  ): number => convertQuantity(quantity, unit, totalQuantityUnit);

  // What a quantity edit drives: the cost follows it, or the rate is re-derived,
  // depending on which side is the user's own figure.
  const recalcFromQuantity = (numeric: number, previousQuantity: number) => {
    if (numeric === 0) return;

    // The rate is per price-unit, so the quantity has to be restated in that
    // unit before dividing — dividing by the raw invoiced quantity would
    // silently rescale a line priced in a different unit.
    const pricedQuantity = quantityInPriceUnit(numeric);
    const pricedQuantityUsable =
      Number.isFinite(pricedQuantity) && pricedQuantity !== 0;
    if (!pricedQuantityUsable && !productDetailsReady) {
      pendingConversionRecalculation.current = {
        type: 'quantity',
        previousQuantity
      };
    }

    // Which side a quantity edit holds still. A rate the user typed, or one
    // that is the product's list price, is a real figure and the cost follows
    // it. A rate merely recovered by dividing a stored cost is not — there the
    // cost is the authority and the rate is re-derived, which is the behaviour
    // stored lines have always had. Typing into Total Cost makes it the anchor
    // either way.
    const priceIsMeaningful =
      priceTouchedRef.current ||
      (priceValue === listPrice && priceUnit === listPriceUnit);

    if (
      totalCostValue !== 0 &&
      (costTypedByUser.current || !priceIsMeaningful)
    ) {
      if (pricedQuantityUsable) {
        applyPrice(Math.abs(div(totalCostValue, pricedQuantity)));
      }
      // A negative quantity makes this a credit/return line, so the cost
      // follows it below zero while the rate stays positive. Negate rather
      // than recompute as quantity × rate, which would drift the saved
      // magnitude. A line that already disagreed on sign is left alone: it
      // predates this rule, and re-signing it here would swing a total the
      // user never touched.
      // Against the quantity as it stood before this edit: `totalQuantityValue`
      // has already moved to the new figure by the time blur runs, so comparing
      // with it would never see the sign change.
      if (
        signsAgree(totalCostValue, previousQuantity) &&
        signsDiffer(totalCostValue, numeric)
      ) {
        applyCost(-totalCostValue);
      }
    } else if (priceValue !== 0 && pricedQuantityUsable) {
      applyCost(mul(pricedQuantity, priceValue));
    }
  };

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    pendingConversionRecalculation.current = null;
    const sanitized = sanitizeNumericInput(e.target.value);
    setTotalQuantityInput(sanitized);

    const val = Number(sanitized);
    const numeric = Number.isNaN(val) ? 0 : val;
    const previousQuantity = totalQuantityValue;
    setTotalQuantityValue(numeric);
    setQtyAutoDerived(false);
    if (numeric !== 0) setQtyError('');
    recalcFromQuantity(numeric, previousQuantity);
  };

  // On focus, seed the display with the full-precision stored value so the
  // user edits/sees the real number rather than the rounded 2-dp display —
  // identical intent to the cost inputs' handleCostFocus.
  const handleQuantityFocus = () => {
    quantityAtFocus.current = totalQuantityValue;
    // Deliberately NOT trimmed like the cost fields: a derived quantity can be
    // legitimately tiny (well below 1e-6), and focus is the only place its exact
    // value is visible. `formatMinPrecision` exists for that case.
    setTotalQuantityInput(
      totalQuantityValue ? formatMinPrecision(totalQuantityValue) : ''
    );
  };

  // On blur, round the quantity display to 2 decimals (e.g. ".0" -> "0.00",
  // "5.5" -> "5.50") — identical to the cost inputs' getCostDisplay/
  // handleCostBlur. The full-precision value is kept in totalQuantityValue for
  // calculations and is shown again on the next focus.
  const handleQuantityBlur = () => {
    if (totalQuantityInput.trim() === '') {
      quantityAtFocus.current = null;
      return;
    }
    // Through the sanitiser, not `Number` directly: the field may still be
    // showing its own grouped display, and "2,000.00" parses as NaN — which the
    // branch below would treat as a partial entry and clear.
    const numeric = Number(sanitizeNumericInput(totalQuantityInput));
    // A partial entry ("-", ".", "-.") sanitizes to a non-numeric string and is
    // stored as 0, so clear the display rather than leaving the stray character
    // on screen contradicting the value the line would save with.
    if (Number.isNaN(numeric)) {
      quantityAtFocus.current = null;
      setTotalQuantityInput('');
      setTotalQuantityValue(0);
      return;
    }
    setTotalQuantityInput(formatQuantityDisplay(numeric));
    setTotalQuantityValue(numeric);
    quantityAtFocus.current = null;
  };

  const handleUnitChange = (value: string) => {
    pendingConversionRecalculation.current = null;
    setTotalQuantityUnit(value);
    if (value) setUnitError('');

    // The quantity's unit is one half of the conversion behind Total Cost, so
    // changing it moves the total exactly as changing the price's unit does.
    if (!totalQuantityValue || !priceValue) return;
    const quantity = convertQuantity(totalQuantityValue, value, priceUnit);
    if (!Number.isFinite(quantity) && !productDetailsReady) {
      pendingConversionRecalculation.current = { type: 'unit' };
      return;
    }
    // A pair that no longer converts must not leave the previous total standing:
    // it would no longer follow from the price and quantity on screen.
    setTotalCostValue(
      Number.isFinite(quantity) ? mul(quantity, priceValue) : 0
    );
  };

  // Display value for a cost input: the raw in-progress string while focused
  // (so the user always sees/edits the REAL value), otherwise the stored value
  // rounded to 2 decimals — identical to the Applications area inputs'
  // `getDisplayValue` (Number(value).toFixed(2)); 0 → "0.00".
  const getCostDisplay = (field: 'price' | 'cost', value: number): string => {
    if (focusedCostField === field && costPendingEdits[field] !== undefined) {
      return costPendingEdits[field] as string;
    }
    // Total Cost stays blank until there is a quantity to work it out from.
    // "$0.00" would claim the product costs nothing, which is a different
    // statement from not knowing yet. Price per Unit is unaffected — "$0.00
    // per [unit]" is the intended default there.
    // Blank, never "0.00", before there is a quantity to work a total out
    // from: 0.00 would claim the product costs nothing, which is a different
    // statement from not knowing yet.
    if (field === 'cost' && !value && !totalQuantityValue) {
      return '';
    }
    return Number.isFinite(value) ? groupDigits(value) : '';
  };

  // On focus, seed the pending edit with the REAL (full-precision) value so the
  // user edits it directly rather than the 2-dp display (area-input parity).
  // A zero Total Cost is a real figure once a quantity exists — at rest it
  // reads "0.00" — so editing it starts from 0 rather than an empty box.
  // Without a quantity there is nothing to show, and Price per Unit keeps the
  // empty start it has always had.
  const seedForEditing = (field: 'price' | 'cost', value: number): string => {
    if (value) return value.toString();
    if (field === 'cost' && totalQuantityValue) return '0';
    return '';
  };

  const handleCostFocus = (field: 'price' | 'cost') => () => {
    const value = field === 'price' ? livePrice.current : liveCost.current;
    signsAgreedBeforeCostEdit.current = signsAgree(
      totalQuantityValue,
      totalCostValue
    );
    setFocusedCostField(field);
    setCostPendingEdits(prev => ({
      ...prev,
      [field]: seedForEditing(field, value)
    }));
  };

  const convertibleQuantity = quantityInPriceUnit(totalQuantityValue);
  const unitsAreIncompatible =
    productDetailsReady &&
    Boolean(totalQuantityValue) &&
    Boolean(priceUnit) &&
    Boolean(totalQuantityUnit) &&
    Number.isNaN(convertibleQuantity);

  // A tick can never outlive the conditions that allowed it — clearing the
  // price, restoring it, or choosing a unit the quantity cannot convert to must
  // not leave a checked box quietly waiting to be saved. Declared here because
  // it reads `unitsAreIncompatible`, which the checkbox is also disabled by.
  useEffect(() => {
    if ((!canSaveNewListPrice || unitsAreIncompatible) && saveAsNewListPrice) {
      setSaveAsNewListPrice(false);
    }
  }, [canSaveNewListPrice, unitsAreIncompatible, saveAsNewListPrice]);

  // The quantity the user typed is the anchor, so the cost field they are not
  // editing is the one recomputed against it.
  const recalcCounterpartCost = (field: 'price' | 'cost', numeric: number) => {
    const quantity = quantityInPriceUnit(totalQuantityValue);
    if (!Number.isFinite(quantity)) {
      if (!productDetailsReady) {
        pendingConversionRecalculation.current = { type: 'cost', field };
      }
      return;
    }

    if (field === 'price') {
      setTotalCostValue(mul(quantity, numeric));
      return;
    }
    // A negative Total Cost against a positive quantity is a credit line
    // entered from the cost side, so the quantity flips to match rather than
    // dragging the rate below zero — unless the line already disagreed on sign
    // before this edit began.
    const rate = Math.abs(div(numeric, quantity));
    setPriceValue(rate);
    if (
      rate !== 0 &&
      signsDiffer(numeric, totalQuantityValue) &&
      signsAgreedBeforeCostEdit.current
    ) {
      const signedQuantity = -totalQuantityValue;
      setTotalQuantityValue(signedQuantity);
      setTotalQuantityInput(formatQuantityDisplay(signedQuantity));
    }
  };

  // Quantity is blank (or previously auto-filled): back it out of the cost pair
  // as they are typed, which is what makes Total Invoiced Quantity appear
  // without the user having to tab out of the field first.
  const recalcDerivedQuantity = (
    field: 'price' | 'cost',
    numeric: number,
    counterpart: number
  ) => {
    if (numeric !== 0 && counterpart !== 0) {
      // cost / rate lands in the price's unit, not the invoiced one, so it has
      // to be converted back before it can stand as the quantity.
      const derivedInPriceUnit =
        field === 'price'
          ? div(counterpart, numeric)
          : div(numeric, counterpart);
      const derivedQuantity = quantityFromPriceUnit(derivedInPriceUnit);
      if (!Number.isFinite(derivedQuantity)) {
        if (!productDetailsReady) {
          pendingConversionRecalculation.current = { type: 'derived', field };
        }
        return;
      }
      setTotalQuantityValue(derivedQuantity);
      setTotalQuantityInput(formatQuantityDisplay(derivedQuantity));
      setQtyAutoDerived(true);
      setQtyError('');
      return;
    }
    if (qtyAutoDerived) {
      // The pair that produced this quantity no longer supports it (one side was
      // cleared). Leaving the old figure standing would let the line save a
      // quantity the two cost fields on screen no longer imply.
      setTotalQuantityValue(0);
      setTotalQuantityInput('');
      setQtyAutoDerived(false);
    }
  };

  // Every one of the three fields recalculates the others as the user types —
  // none of them waits for blur, so the trio can never read as inconsistent.
  // Which field gives way depends on whether the quantity is the user's own: a
  // typed quantity is the anchor and the counterpart cost is recomputed, while a
  // blank or back-computed quantity is what yields to the two cost fields.
  const handleCostChange = (field: 'price' | 'cost') => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    pendingConversionRecalculation.current = null;
    const sanitized =
      field === 'price'
        ? sanitizeRateInput(e.target.value)
        : sanitizeNumericInput(e.target.value);
    setCostPendingEdits(prev => ({ ...prev, [field]: sanitized }));

    const numeric = Number(sanitized) || 0;
    const counterpart = field === 'price' ? totalCostValue : priceValue;
    const qtyIsAnchor = totalQuantityValue !== 0 && !qtyAutoDerived;

    if (field === 'price') {
      priceTouchedRef.current = true;
      setPriceValue(numeric);
    } else {
      costTypedByUser.current = true;
      setTotalCostValue(numeric);
    }

    if (qtyIsAnchor) {
      recalcCounterpartCost(field, numeric);
      return;
    }

    recalcDerivedQuantity(field, numeric, counterpart);
  };

  // Choosing a different unit for the price changes what the price means, so
  // the total has to follow it.
  const handlePriceUnitChange = (unit: string) => {
    pendingConversionRecalculation.current = null;
    priceUnitPinnedRef.current = true;
    setPriceUnit(unit);

    if (!totalQuantityValue || !priceValue) return;
    const quantity = quantityInPriceUnit(totalQuantityValue, unit);
    if (!Number.isFinite(quantity) && !productDetailsReady) {
      pendingConversionRecalculation.current = { type: 'unit' };
      return;
    }
    setTotalCostValue(
      Number.isFinite(quantity) ? mul(quantity, priceValue) : 0
    );
  };

  const handleResetPrice = () => {
    pendingConversionRecalculation.current = null;
    // The restored figure is the list price, not something the user typed.
    priceTouchedRef.current = false;
    setPriceValue(resetValues.price);
    setPriceUnit(resetValues.unit);
    setSaveAsNewListPrice(false);
    // Leave the drawer self-consistent: a restored price that still showed the
    // old total would read as a contradiction.
    // Against the unit being restored, not the one still in state — setPriceUnit
    // above has not applied yet, so the default argument would be the old unit.
    const pricedQuantity = quantityInPriceUnit(
      totalQuantityValue,
      resetValues.unit
    );
    if (Number.isFinite(pricedQuantity)) {
      setTotalCostValue(mul(pricedQuantity, resetValues.price));
    } else if (!productDetailsReady) {
      pendingConversionRecalculation.current = { type: 'reset' };
    }
    setFocusedCostField(null);
    setCostPendingEdits({});
  };

  useEffect(() => {
    const pending = pendingConversionRecalculation.current;
    if (!productDetailsReady || !pending) return;

    pendingConversionRecalculation.current = null;
    if (pending.type === 'quantity') {
      recalcFromQuantity(totalQuantityValue, pending.previousQuantity);
      return;
    }
    if (pending.type === 'cost') {
      recalcCounterpartCost(
        pending.field,
        pending.field === 'price' ? priceValue : totalCostValue
      );
      return;
    }
    if (pending.type === 'derived') {
      recalcDerivedQuantity(
        pending.field,
        pending.field === 'price' ? priceValue : totalCostValue,
        pending.field === 'price' ? totalCostValue : priceValue
      );
      return;
    }

    const pricedQuantity = quantityInPriceUnit(totalQuantityValue);
    if (Number.isFinite(pricedQuantity)) {
      setTotalCostValue(mul(pricedQuantity, priceValue));
    } else if (pending.type === 'unit') {
      setTotalCostValue(0);
    }
    // Replay the latest edit's calculation semantics after metadata arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productDetailsReady]);

  // All three fields are committed on change, so blur only drops the pending
  // edit to let the input fall back to its rounded 2-dp display.
  const handleCostBlur = (field: 'price' | 'cost') => () => {
    setFocusedCostField(null);
    setCostPendingEdits(prev => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleSave = () => {
    let hasError = false;

    if (!totalQuantityValue) {
      setQtyError('Quantity cannot be 0');
      hasError = true;
    } else {
      setQtyError('');
    }

    if (totalQuantityUnit) {
      setUnitError('');
    } else {
      setUnitError('Unit is required');
      hasError = true;
    }

    // The price row already shows why, but the line must not save while its two
    // units cannot convert: Total Cost no longer follows from what is on screen.
    if (unitsAreIncompatible) {
      hasError = true;
    }

    if (hasError) return;

    onSave({
      ...(existingProduct?.id ? { id: existingProduct.id } : {}),
      // Preserve the trackingId on edit. The invoice-service update matches an
      // existing line item by (trackingId AND id); dropping it makes the
      // backend treat the edited product as new, producing a duplicate entry
      // with a null trackingId that NPEs downstream → 500 (FR-113).
      ...(existingProduct?.trackingId
        ? { trackingId: existingProduct.trackingId }
        : {}),
      productId: catalogProduct.id,
      productName: catalogProduct.name,
      manufacturerName:
        productDetails?.manufacturer || existingProduct?.manufacturerName,
      custom: catalogProduct.custom,
      totalQuantityValue,
      totalQuantityUnit,
      totalCostValue: canWriteCost ? totalCostValue : 0,
      ...(canWriteCost
        ? {
            priceValue,
            priceUnit,
            // The effect above clears this, but a save in the same render pass
            // would otherwise still carry a tick the user can no longer see.
            saveAsNewListPrice: saveAsNewListPrice && !unitsAreIncompatible,
            packageWeight
          }
        : {
            // Without cost-write permission the Cost section never rendered, so
            // there is no intent to record — but the rate already on the line
            // has to survive, or saving from this drawer would clear a stored
            // price this user was never shown.
            priceValue: existingProduct?.priceValue,
            priceUnit: existingProduct?.priceUnit,
            saveAsNewListPrice: false
          }),
      // Carry the catalog's density onto the line item so the API payload can
      // drive the invoice service's unit math (FR-095). Fall back to any value
      // already on the product (edit flow) if the catalog fetch hasn't resolved.
      productDensity: productDetails?.density ?? existingProduct?.productDensity
    });
  };

  return (
    <Drawer
      zIndex={zIndex}
      className={`cwf-invoice-product-drawer${
        suppressAnimation ? ' instant-drawer' : ''
      }${visible ? '' : ' cwf-invoice-product-drawer-closing'}`}
      open={visible}
      placement="right"
      closable={false}
      mask={mask}
      maskClosable={false}
      onClose={onClose}
      width={DRAWER_WIDTH}
      footer={
        <div className="flex justify-between">
          <Button
            type={ButtonType.outline}
            size="large"
            onClick={onClose}
            icon={<ChevronLeftIcon />}
          >
            {isEditing ? 'Cancel' : 'Back'}
          </Button>
          <Button
            type={ButtonType.primary}
            size="large"
            onClick={handleSave}
            disabled={!totalQuantityValue || !totalQuantityUnit}
          >
            {isEditing ? 'Update Product Details' : 'Save Product Details'}
          </Button>
        </div>
      }
    >
      <div className="relative">
        <div className="absolute top-0 right-0 m-3">
          <Button
            type={ButtonType.link}
            onClick={onClose}
            icon={<CloseIcon />}
          />
        </div>
      </div>

      <div className="px-8 pt-8">
        <div className="gap-4 mb-6" data-testid="product-container">
          <h1 className="text-xl font-semibold block leading-snug">
            {catalogProduct.name}
          </h1>
          <p className="text-base leading-6 text-neutral-60">
            {productDetails?.manufacturer || ''}
          </p>
        </div>

        <ProductQuantitySection
          totalQuantityInput={totalQuantityInput}
          totalQuantityUnit={totalQuantityUnit}
          units={units}
          qtyError={qtyError}
          unitError={unitError}
          isUnitDropdownOpen={isUnitDropdownOpen}
          setIsUnitDropdownOpen={setIsUnitDropdownOpen}
          onQuantityChange={handleQuantityChange}
          onQuantityFocus={handleQuantityFocus}
          onQuantityBlur={handleQuantityBlur}
          onUnitChange={handleUnitChange}
          labelDocument={labelDocument}
          openFile={openFile}
        />

        {canWriteCost && (
          <ProductCostSection
            priceValue={priceValue}
            priceUnit={priceUnit}
            totalCostValue={totalCostValue}
            totalQuantityUnit={totalQuantityUnit}
            units={units}
            unitsAreIncompatible={unitsAreIncompatible}
            isResetDisabled={isResetDisabled}
            canSaveNewListPrice={canSaveNewListPrice}
            saveAsNewListPrice={saveAsNewListPrice}
            setSaveAsNewListPrice={setSaveAsNewListPrice}
            isPriceUnitDropdownOpen={isPriceUnitDropdownOpen}
            setIsPriceUnitDropdownOpen={setIsPriceUnitDropdownOpen}
            getCostDisplay={getCostDisplay}
            onCostFocus={handleCostFocus}
            onCostChange={handleCostChange}
            onCostBlur={handleCostBlur}
            onPriceUnitChange={handlePriceUnitChange}
            onResetPrice={handleResetPrice}
          />
        )}
      </div>
    </Drawer>
  );
};

export default FillInvoiceProductDrawer;
